import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import moment from 'moment';
import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import type { ListItemCardProps } from 'voyager';
import { SpaCategoryDetailTemplate } from 'voyager';

import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useGetSpaTreatmentsPage } from '@features/spa/hooks';
import { useGetSpaTreatments } from '@features/spa/hooks/useGetAvailableSpaTreatments';
import { hasAvailability } from '@utils/helpers';

import type { SpaTreatmentsCategoryV2Props } from './SpaTreatmentsCategoryV2.types';

export const SpaTreatmentsCategoryV2: FC<SpaTreatmentsCategoryV2Props> = ({
  property,
  journeyParams,
  language,
  category,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const { spaTreatments = [], isFetching } = useGetSpaTreatments(
    property.id,
    language,
    selectedDateString,
    Number(category.id)
  );

  const { data: treatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const fallbackImageUrl = treatmentsPage?.heroBackgroundImage?.url;

  const items: ListItemCardProps[] = spaTreatments
    .sort((a, b) => a.priority - b.priority)
    .map((tr) => {
      const isAvailable = hasAvailability(tr, selectedDateString);
      return {
        id: String(tr.id),
        name: tr.name,
        image: {
          src: tr.image?.url || fallbackImageUrl,
          alt: tr.name,
        },
        price:
          tr.price?.amount != null
            ? {
                amount: tr.price.amount,
                currencyCode: tr.price.currencyCode,
                label: t('common.from'),
              }
            : undefined,
        unavailable: !isFetching && !isAvailable,
      };
    });

  const handleSelectItem = useCallback(
    (id: string) => {
      router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
    },
    [router]
  );

  const handleEditSearch = useCallback(() => {
    router.push({
      pathname: Routes.SPA_TREATMENT_LIST,
      query: { arrivalDate: selectedDateString },
    });
  }, [router, selectedDateString]);

  const journeyBar = <JourneyPickerV2Connected dateMode="single" dateContext="spa" showGuests />;

  return (
    <>
      <Spinner showDelayMessage isActive={isFetching} />
      <SpaCategoryDetailTemplate
        categoryName={category.name}
        categoryDescription={category.description}
        categoryImageUrl={category.image?.url || fallbackImageUrl}
        items={items}
        isLoading={isFetching}
        onSelectItem={handleSelectItem}
        onEditSearch={handleEditSearch}
        journeyBar={journeyBar}
        labels={{
          buttonLabel: t('common.moreDetails'),
          unavailableLabel: t('spa.soldOut'),
          editSearchLabel: t('common.findOtherDates'),
        }}
      />
    </>
  );
};
